// Thin client for the Healife (dcp_ai_fitness) backend.
// Mirrors lib/services/api_service.dart — same envelope: { error, message, code, results }.

const BASE_URL = 'https://dcp-humanos-410d767c97ce.herokuapp.com/api';

const TOKEN_KEY = 'dcp_admin_token';
const DEVICE_ID_KEY = 'dcp_admin_device_id';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getDeviceId = () => localStorage.getItem(DEVICE_ID_KEY);

export const setToken = (token) =>
  token ? localStorage.setItem(TOKEN_KEY, token) : localStorage.removeItem(TOKEN_KEY);

export const setDeviceId = (deviceId) =>
  deviceId ? localStorage.setItem(DEVICE_ID_KEY, deviceId) : localStorage.removeItem(DEVICE_ID_KEY);

export const clearCredentials = () => {
  setToken(null);
  setDeviceId(null);
};

// A JWT has three dot-separated segments; anything else is treated as a deviceId.
export const looksLikeJwt = (value) => value.split('.').length === 3;

class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function rawRequest(path, { method = 'GET', body, token, lang = 'tr' } = {}) {
  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      // Dil HER ZAMAN acikca gonderilir; varsayilan Turkce. Bu satir olmadan
      // tarayici kendi dilini gonderiyor, backend icerigi o dile cevirerek
      // donduruyor ve form kaydedildiginde ceviri kanonik Turkce'nin USTUNE
      // yaziliyor — kaynak metin geri donusu olmadan kayboluyor.
      //
      // Turkce disi bir dil YALNIZCA okuma icin gecilir (bkz. AdminList
      // onizleme modu); duzenleme her zaman Turkce uzerinden yapilir.
      'Accept-Language': lang,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  let payload;
  try {
    payload = await response.json();
  } catch {
    throw new ApiError('Sunucudan geçersiz yanıt alındı.', response.status);
  }

  if (payload.error === true || !response.ok) {
    throw new ApiError(payload.message || 'İstek başarısız oldu.', response.status);
  }

  return payload.results;
}

/** Exchange a deviceId for a fresh JWT and persist it. */
export async function loginWithDeviceId(deviceId) {
  const results = await rawRequest('/user/login', {
    method: 'POST',
    body: { deviceId },
  });
  setToken(results.token);
  setDeviceId(deviceId);
  return results.token;
}

/**
 * Authenticated request. On a 401 the stored token has expired, so re-login with
 * the stored deviceId and retry once — that is what keeps the panel link working
 * indefinitely without asking for credentials again.
 */
async function request(path, options = {}) {
  try {
    return await rawRequest(path, { ...options, token: getToken() });
  } catch (error) {
    const deviceId = getDeviceId();
    if (error.status !== 401 || !deviceId) throw error;

    await loginWithDeviceId(deviceId);
    return rawRequest(path, { ...options, token: getToken() });
  }
}

/** Fetch the current user; used to verify the credential belongs to an admin. */
export async function getMe() {
  const results = await request('/user/me');
  return results.user;
}

/** `lang` yalnizca goruntuleme icindir; duzenleme her zaman Turkce uzerinden. */
export async function getExploreItems(category, limit = 100, lang = 'tr') {
  const params = new URLSearchParams({ page: '1', limit: String(limit), category });
  const results = await request(`/explore?${params.toString()}`, { lang });
  return results.items ?? [];
}

export const createExploreItem = (data) =>
  request('/explore', { method: 'POST', body: data });

export const updateExploreItem = (id, data) =>
  request(`/explore/${id}`, { method: 'PATCH', body: data });

export const deleteExploreItem = (id) =>
  request(`/explore/${id}`, { method: 'DELETE' });

/** Ceviriyi yeniden tetikler. Takilmis/hatali kayitlar ve Faz 2 oncesi icerik icin. */
export const translateExploreItem = (id) =>
  request(`/explore/${id}/translate`, { method: 'POST' });

export async function generateExploreItem(category) {
  const results = await request('/explore/generate', {
    method: 'POST',
    body: { category },
  });
  return results.generated;
}

/** Imzali dogrudan yukleme icin tek kullanimlik imza uretir. */
export const getUploadSignature = (kind) =>
  request('/explore/upload-signature', { method: 'POST', body: { kind } });

/** Onay bekleyen AI taslaklari. Liste her zaman Turkce: onay kanonik metin uzerinden. */
export async function getDrafts() {
  const results = await request('/explore/drafts');
  return results.items ?? [];
}

/** Yayina alir ve ceviriyi baslatir. Yayinin tek yolu. */
export const approveDraft = (id) =>
  request(`/explore/${id}/approve`, { method: 'POST' });

/** Notla: sonraki calismada (en gec 1 saat) notla yeniden uretilir. Notsuz: taslak atilir. */
export const rejectDraft = (id, note) =>
  request(`/explore/${id}/reject`, { method: 'POST', body: { note } });
